// src/components/AdminUserManagement.js
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminUserManagement.css';

function AdminUserManagement() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        username: '',
        password: '',
        isAdmin: false
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/admin/users');
            setUsers(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch users. You may not have admin privileges.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const resetForm = () => {
        setFormData({
            firstName: '',
            middleName: '',
            lastName: '',
            username: '',
            password: '',
            isAdmin: false
        });
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            await api.post('/api/admin/users', formData);
            fetchUsers();
            setShowAddForm(false);
            resetForm();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add user');
        }
    };

    const handleEditClick = (user) => {
        setEditingUser(user.id);
        setFormData({
            firstName: user.firstName,
            middleName: user.middleName || '',
            lastName: user.lastName,
            username: user.username,
            password: '', // Password field is empty for editing
            isAdmin: user.isAdmin
        });
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            // Only send password if it was changed
            const updateData = { ...formData };
            if (!updateData.password) {
                delete updateData.password;
            }

            await api.put(`/api/admin/users/${editingUser}`, updateData);
            fetchUsers();
            setEditingUser(null);
            resetForm();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) {
            return;
        }

        try {
            await api.delete(`/api/admin/users/${userId}`);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete user');
        }
    };

    if (loading) return <div className="loading">Loading users...</div>;

    return (
        <div className="admin-user-management">
            <div className="admin-header">
                <h2>User Management</h2>
                <button className="add-user-btn" onClick={() => {
                    resetForm();
                    setShowAddForm(true);
                }}>Add New User</button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {showAddForm && (
                <div className="user-form">
                    <h3>Add New User</h3>
                    <form onSubmit={handleAddUser}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Middle Name</label>
                                <input
                                    type="text"
                                    name="middleName"
                                    value={formData.middleName}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Username</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Password</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-group admin-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="isAdmin"
                                        checked={formData.isAdmin}
                                        onChange={handleChange}
                                    />
                                    Admin User
                                </label>
                            </div>
                        </div>

                        <div className="form-buttons">
                            <button type="submit" className="save-btn">Add User</button>
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setShowAddForm(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {users.length === 0 ? (
                <div className="no-data">No users found</div>
            ) : (
                <div className="users-table">
                    <table>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Username</th>
                            <th>Role</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {users.map(user => (
                            <React.Fragment key={user.id}>
                                <tr>
                                    <td>{`${user.firstName} ${user.middleName ? user.middleName + ' ' : ''}${user.lastName}`}</td>
                                    <td>{user.username}</td>
                                    <td>{user.isAdmin ? 'Admin' : 'User'}</td>
                                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <button
                                            className="edit-btn"
                                            onClick={() => handleEditClick(user)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="delete-btn"
                                            onClick={() => handleDeleteUser(user.id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>

                                {editingUser === user.id && (
                                    <tr className="edit-row">
                                        <td colSpan="5">
                                            <form onSubmit={handleUpdateUser} className="edit-form">
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>First Name</label>
                                                        <input
                                                            type="text"
                                                            name="firstName"
                                                            value={formData.firstName}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Middle Name</label>
                                                        <input
                                                            type="text"
                                                            name="middleName"
                                                            value={formData.middleName}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Last Name</label>
                                                        <input
                                                            type="text"
                                                            name="lastName"
                                                            value={formData.lastName}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Username</label>
                                                        <input
                                                            type="text"
                                                            name="username"
                                                            value={formData.username}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>New Password (leave blank to keep current)</label>
                                                        <input
                                                            type="password"
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group admin-checkbox">
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                name="isAdmin"
                                                                checked={formData.isAdmin}
                                                                onChange={handleChange}
                                                            />
                                                            Admin User
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="form-buttons">
                                                    <button type="submit" className="save-btn">Update</button>
                                                    <button
                                                        type="button"
                                                        className="cancel-btn"
                                                        onClick={() => {
                                                            setEditingUser(null);
                                                            resetForm();
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </form>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AdminUserManagement;
